/**
 * Phase 9.5 — 👤 Profile Preview Modal (Live Beams)
 * --------------------------------------------------
 * Tap any avatar inside a Live Beam (speakers / listeners / chat / gift
 * feed / leaderboard) to open this compact modal. Surfaces enough info
 * to decide whether to follow, view full profile, or send a gift, without
 * leaving the live.
 *
 * Data source: GET /api/artist/{user_id} (already enriched with badges,
 * follower count, feature_count, collab_count, genre, bio).
 * Follow state: GET / POST / DELETE /api/artist/{user_id}/follow.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

// Pretty role chips derived from the user's badges + counts.
const roleChipsFor = (u) => {
  const chips = [];
  const badges = u.badges || [];
  if (badges.includes("founder")) chips.push({ label: "👑 Founder", cls: "from-amber-500 to-orange-600" });
  if (badges.includes("admin"))   chips.push({ label: "🛡️ Admin",   cls: "from-rose-500 to-pink-600" });
  if (badges.includes("verified_artist") || u.verified)
    chips.push({ label: "✓ Verified", cls: "from-blue-500 to-cyan-600" });
  if (badges.includes("host"))    chips.push({ label: "🎙️ Host",    cls: "from-pink-500 to-purple-600" });
  if (badges.includes("dj"))      chips.push({ label: "🎧 DJ",       cls: "from-purple-500 to-indigo-600" });
  if (badges.includes("producer"))chips.push({ label: "🎚 Producer", cls: "from-emerald-500 to-teal-600" });
  if (badges.includes("business"))chips.push({ label: "💼 Business", cls: "from-slate-500 to-gray-600" });
  // Default — every account that's released a track is an Artist.
  if (chips.length === 0 && (u.feature_count || u.tracks_count || u.collab_count)) {
    chips.push({ label: "🎵 Artist", cls: "from-pink-500 to-purple-600" });
  }
  return chips;
};

const ProfilePreviewModal = ({ userId, viewerId, onClose, onSendGift, monitorAction, isMonitor }) => {
  const [user, setUser] = useState(null);
  const [trackCount, setTrackCount] = useState(0);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/artist/${userId}`);
        if (cancelled) return;
        setUser(r.data?.user || null);
        setTrackCount((r.data?.submissions || []).length);
      } catch (_e) {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Follow status — only if viewer is signed in and not viewing self.
      if (viewerId && viewerId !== userId) {
        try {
          const s = await api.get(`/artist/${userId}/follow-status`, {
            params: { follower_id: viewerId },
          });
          if (!cancelled) setFollowing(!!s.data?.is_following);
        } catch (_e) {}
      }
    })();
    return () => { cancelled = true; };
  }, [userId, viewerId]);

  // Coerce FastAPI 422 detail arrays (or any object) to a safe string so
  // toast.error never receives a non-string child that would crash React.
  const errorMessage = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(", ") || fallback;
    if (d && typeof d === "object") return d.msg || fallback;
    return fallback;
  };

  const toggleFollow = async () => {
    if (!viewerId) { toast.error("Sign in to follow"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (following) {
        await api.delete(`/artist/${userId}/follow`, {
          params: { follower_id: viewerId },
        });
        setFollowing(false);
        toast.success("Unfollowed");
      } else {
        await api.post(`/artist/${userId}/follow`, { follower_id: viewerId });
        setFollowing(true);
        toast.success(`Following ${user?.artist_name || user?.name || "user"}`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't update follow"));
    } finally {
      setBusy(false);
    }
  };

  if (!userId) return null;

  const chips = user ? roleChipsFor(user) : [];
  const isSelf = viewerId && viewerId === userId;
  const followers = user?.followers?.length ?? user?.follower_count ?? 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      data-testid="profile-preview-modal"
      onClick={onClose}
    >
      <Card
        className="w-full sm:max-w-sm bg-[#140a1f] border-pink-500/40 shadow-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="p-8 text-center text-pink-200/60 text-sm" data-testid="profile-preview-loading">
            Loading profile…
          </div>
        )}

        {!loading && !user && (
          <div className="p-6 text-center" data-testid="profile-preview-error">
            <p className="text-pink-200/70 text-sm mb-3">Couldn't load this profile.</p>
            <Button onClick={onClose} variant="outline" className="text-pink-200 border-pink-500/40">
              Close
            </Button>
          </div>
        )}

        {!loading && user && (
          <>
            <div className="relative h-20 bg-gradient-to-br from-pink-500/30 via-purple-500/20 to-amber-500/20">
              <button
                onClick={onClose}
                className="absolute top-2 right-2 text-pink-200/80 hover:text-white text-xl leading-none w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center"
                aria-label="Close"
                data-testid="profile-preview-close"
              >
                ×
              </button>
            </div>

            <div className="px-5 pb-5 -mt-10">
              <img
                src={getProfilePhotoUrl(user)}
                alt={user.artist_name || user.name}
                className="w-20 h-20 rounded-full object-cover border-4 border-[#140a1f] shadow-lg shadow-pink-500/30"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
                data-testid="profile-preview-photo"
              />
              <h2
                className="mt-2 text-xl font-black text-white truncate"
                data-testid="profile-preview-name"
              >
                {user.artist_name || user.name || "Anonymous"}
              </h2>
              {user.genre && (
                <p className="text-xs text-pink-300/70">{user.genre}</p>
              )}

              {chips.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="profile-preview-roles">
                  {chips.map((c) => (
                    <span
                      key={c.label}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r ${c.cls} text-white`}
                    >
                      {c.label}
                    </span>
                  ))}
                </div>
              )}

              {user.bio && (
                <p
                  className="mt-3 text-xs text-pink-200/80 leading-relaxed line-clamp-3"
                  data-testid="profile-preview-bio"
                >
                  {user.bio}
                </p>
              )}

              <div className="mt-3 flex items-center gap-3 text-[11px] text-pink-200/70 flex-wrap">
                <span data-testid="profile-preview-followers">
                  <span className="text-white font-bold">{followers.toLocaleString()}</span> followers
                </span>
                <span data-testid="profile-preview-tracks">
                  <span className="text-white font-bold">{trackCount.toLocaleString()}</span> tracks
                </span>
                {user.feature_count ? (
                  <span data-testid="profile-preview-features">
                    🎖️ <span className="text-white font-bold">{user.feature_count}</span> features
                  </span>
                ) : null}
                {user.collab_count ? (
                  <span data-testid="profile-preview-collabs">
                    🤝 <span className="text-white font-bold">{user.collab_count}</span> collabs
                  </span>
                ) : null}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {!isSelf && (
                  <Button
                    onClick={toggleFollow}
                    disabled={busy}
                    className={`col-span-2 font-bold ${
                      following
                        ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                        : "bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white"
                    }`}
                    data-testid="profile-preview-follow-btn"
                  >
                    {busy ? "…" : following ? "✓ Following" : "+ Follow"}
                  </Button>
                )}
                <Link to={`/artist/${userId}`} onClick={onClose} className="col-span-1" data-testid="profile-preview-view-full">
                  <Button variant="outline" className="w-full text-pink-200 border-pink-500/40 hover:bg-pink-500/10 text-xs">
                    View Profile
                  </Button>
                </Link>
                <Link to={`/artist/${userId}?tab=tracks`} onClick={onClose} className="col-span-1" data-testid="profile-preview-view-tracks">
                  <Button variant="outline" className="w-full text-pink-200 border-pink-500/40 hover:bg-pink-500/10 text-xs">
                    🎵 View Tracks
                  </Button>
                </Link>
                {/* Phase 9.7.2 — 🎁 Tap avatar → mini profile → Send Gift */}
                {!isSelf && onSendGift && (
                  <Button
                    onClick={() => { onSendGift(user); onClose?.(); }}
                    className="col-span-2 bg-gradient-to-r from-amber-500 to-rose-500 text-white font-bold"
                    data-testid="profile-preview-send-gift"
                  >
                    🎁 Send Gift
                  </Button>
                )}
                {/* Phase 9.7.2 — 🎛️ Host-only "Make/Remove Monitor" action */}
                {!isSelf && monitorAction && (
                  <Button
                    onClick={() => { monitorAction(user, !isMonitor); onClose?.(); }}
                    variant="outline"
                    className="col-span-2 border-cyan-400/50 text-cyan-100 hover:bg-cyan-500/15 text-xs font-bold"
                    data-testid={isMonitor ? "profile-preview-remove-monitor" : "profile-preview-make-monitor"}
                  >
                    {isMonitor ? "🎛️ Remove Monitor" : "🎛️ Make Monitor"}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ProfilePreviewModal;
